/**
 * useFilaConfig Hook
 *
 * Hook customizado para gerenciar configurações do sistema de fila digital
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { FilaConfig, OrderType, VisibleColumns, WebSocketUpdateMode, IntervalConfig } from '../types/config';
import { saveFilaConfig, loadFilaConfig, clearFilaConfig } from '../utils/fila-config-storage';
import { initializeDebugTools, setupStorageDebugListeners } from '../utils/debug-tools';

export function useFilaConfig() {
  // Inicializar ferramentas de debug (apenas uma vez)
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      initializeDebugTools();
      setupStorageDebugListeners();
    }
  }, []);

  const [config, setConfig] = useState<FilaConfig>(() => {
    console.log('🔧 [useFilaConfig] Inicializando com configuração:');
    const initialConfig = loadFilaConfig();
    console.log('📋 [useFilaConfig] Configuração inicial:', initialConfig);
    return initialConfig;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs para debounce e controle de loops
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);

  // Salvar configurações imediatamente (sem debounce)
  const saveImmediately = useCallback((newConfig: Partial<FilaConfig>) => {
    // Prevenir loops com flag de controle
    if (isUpdatingRef.current) {
      console.log('🔄 Já está atualizando, ignorando para prevenir loop');
      return false;
    }

    try {
      isUpdatingRef.current = true;

      setConfig(currentConfig => {
        // Prevenir loops com comparação profunda
        const hasChanges = Object.keys(newConfig).some(key => {
          const currentValue = currentConfig[key as keyof FilaConfig];
          const newValue = newConfig[key as keyof FilaConfig];
          return JSON.stringify(currentValue) !== JSON.stringify(newValue);
        });

        if (!hasChanges) {
          console.log('🔄 Nenhuma mudança detectada, ignorando salvamento');
          return currentConfig;
        }

        const updatedConfig = { ...currentConfig, ...newConfig };
        console.log('💾 Salvando configurações imediatamente:', updatedConfig);

        if (saveFilaConfig(updatedConfig)) {
          return updatedConfig;
        } else {
          setError('Erro ao salvar configurações');
          return currentConfig; // Retorna config anterior se falhar
        }
      });

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      return false;
    } finally {
      // Limpar flag de atualização após um pequeno delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100); // Aumentado para 100ms para prevenir race conditions
    }
  }, []);

  // Atualizar configurações com debounce (para múltiplas alterações rápidas)
  const updateConfig = useCallback((newConfig: Partial<FilaConfig>) => {
    // Limpar timeout anterior
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Configurar novo timeout
    debounceTimeoutRef.current = setTimeout(() => {
      saveImmediately(newConfig);
    }, 300); // 300ms de debounce
  }, [saveImmediately]);

  // Forçar salvamento imediato (sem debounce)
  const forceUpdateConfig = useCallback((newConfig: Partial<FilaConfig>) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    return saveImmediately(newConfig);
  }, [saveImmediately]);

  // Configurar colunas visíveis (salvamento imediato)
  const setVisibleColumns = useCallback((columns: Partial<VisibleColumns>) => {
    const updatedColumns = { ...config.visibleColumns, ...columns };
    return forceUpdateConfig({ visibleColumns: updatedColumns });
  }, [config.visibleColumns, forceUpdateConfig]);

  // Configurar tipo de pedido (salvamento imediato)
  const setOrderType = useCallback((orderType: OrderType) => {
    return forceUpdateConfig({ orderType });
  }, [forceUpdateConfig]);

  // Configurar troca automática de intervalo (salvamento imediato)
  const setAutoIntervalChange = useCallback((autoIntervalChange: boolean, selectedInterval?: string, selectedIntervalName?: string) => {
    console.log('🔄 setAutoIntervalChange called:', {
      autoIntervalChange,
      selectedInterval,
      selectedIntervalName,
      currentConfig: config,
    });

    return forceUpdateConfig({
      autoIntervalChange,
      // NÃO limpar selectedInterval e selectedIntervalName quando autoIntervalChange=true
      // porque podemos precisar deles para compatibilidade
      selectedInterval: autoIntervalChange ? config.selectedInterval : selectedInterval,
      selectedIntervalName: autoIntervalChange ? config.selectedIntervalName : selectedIntervalName,
    });
  }, [forceUpdateConfig, config]);

  // Configurar estabelecimento (salvamento imediato)
  const setEstablishmentId = useCallback((establishmentId: string) => {
    return forceUpdateConfig({ establishmentId });
  }, [forceUpdateConfig]);

  // Configurar modo de atualização do WebSocket (salvamento imediato)
  const setWebSocketUpdateMode = useCallback((websocketUpdateMode: WebSocketUpdateMode) => {
    return forceUpdateConfig({ websocketUpdateMode });
  }, [forceUpdateConfig]);

  // Configurar intervalo atual completo (dados completos)
  const setCurrentInterval = useCallback((interval: IntervalConfig | null) => {
    const newConfig = {
      currentInterval: interval || undefined,
      selectedInterval: interval ? undefined : config.selectedInterval,
    };
    return forceUpdateConfig(newConfig);
  }, [config.selectedInterval, forceUpdateConfig]);

  // Configurar tamanho do texto do número do pedido
  const setOrderNumberTextSize = useCallback((orderNumberTextSize: number) => {
    return forceUpdateConfig({ orderNumberTextSize });
  }, [forceUpdateConfig]);

  // Configurar tamanho do texto do título das filas
  const setQueueTitleTextSize = useCallback((queueTitleTextSize: number) => {
    return forceUpdateConfig({ queueTitleTextSize });
  }, [forceUpdateConfig]);

  // Resetar para configurações padrão
  const resetConfig = useCallback(() => {
    setIsLoading(true);
    setError(null);

    try {
      clearFilaConfig();
      const defaultConfig = loadFilaConfig(); // Carrega o default
      setConfig(defaultConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao resetar configurações');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Removido o uso do hook dentro de useEffect - isso viola as regras dos hooks
  // O estabelecimento será definido nos componentes individuais quando necessário

  // Função para sincronizar establishmentId quando disponível externamente
  const syncEstablishmentId = useCallback((establishmentId: string) => {
    if (!establishmentId || establishmentId === config.establishmentId) {
      return;
    }

    console.log('🔄 Sincronizando establishmentId:', establishmentId);

    // Usar forceUpdateConfig para evitar loops
    forceUpdateConfig({ establishmentId });
  }, [config.establishmentId, forceUpdateConfig]);

  // Prevenir reloads desnecessários ouvindo eventos beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Se estiver no meio de uma atualização, prevenir reload acidental
      if (isUpdatingRef.current) {
        e.preventDefault();
        e.returnValue = 'Configurações estão sendo salvas. Tem certeza que deseja sair?';
      }
    };

    // Adicionar listener only quando estiver atualizando
    if (isUpdatingRef.current) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [config]); // Recriar listener quando config mudar

  // Cleanup do debounce timeout ao desmontar
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    config,
    updateConfig,
    forceUpdateConfig,
    setVisibleColumns,
    setOrderType,
    setAutoIntervalChange,
    setEstablishmentId,
    setWebSocketUpdateMode,
    setCurrentInterval,
    setOrderNumberTextSize,
    setQueueTitleTextSize,
    syncEstablishmentId,
    resetConfig,
    isLoading,
    error,
    clearError: () => setError(null),
  };
}